export const paths = {
  home: {
    path: '/',
    getHref: () => '/',
  },
  auth: {
    register: {
      path: '/register',
      getHref: (redirectTo?: string | null | undefined) =>
        `/register${
          redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''
        }`,
    },
    login: {
      path: '/login',
      getHref: (redirectTo?: string | null | undefined) =>
        `/login${
          redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''
        }`,
    },
    resetPassword: {
      path: '/reset-password',
      getHref: () => '/reset-password',
    },
    updatePassword: {
      path: '/update-password',
      getHref: (from?: string) =>
        `/update-password${from ? `?from=${encodeURIComponent(from)}` : ''}`,
    },
  },
  org: {
    create: {
      path: '/org/create',
      getHref: (redirectTo?: string | null | undefined) =>
        `/org/create${
          redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''
        }`,
    },
    manageInvitation: {
      path: '/manage-org-invitation',
      getHref: (token?: string) =>
        `/manage-org-invitation${token ? `?token=${token}` : ''}`,
    },
    memberManagement: {
      path: '/member-management',
      getHref: () => '/member-management',
    },
  },
  projects: {
    hub: {
      path: '/projects',
      getHref: () => '/projects',
    },
    create: {
      base: {
        path: '/projects/create',
        getHref: () => '/projects/create',
      },
      selectBranch: {
        path: '/projects/create/select-branch',
        getHref: () => '/projects/create/select-branch',
      },
      selectSubcategory: {
        path: '/projects/create/select-subcategory',
        getHref: () => '/projects/create/select-subcategory',
      },
      kpi: {
        path: '/projects/create/kpi',
        getHref: () => '/projects/create/kpi',
      },
      kpiSummary: {
        path: '/projects/create/kpi/summary',
        getHref: () => '/projects/create/kpi/summary',
      },
      inviteUsers: {
        path: '/projects/create/invite-users',
        getHref: () => '/projects/create/invite-users',
      },
      submit: {
        path: '/projects/create/submit',
        getHref: () => '/projects/create/submit',
      },
    },
    details: {
      base: {
        path: '/projects/:projectId',
        getHref: (projectId: string) => `/projects/${projectId}`,
      },
      memberManagement: {
        path: '/projects/:projectId/member-management',
        getHref: (projectId: string | number) =>
          `/projects/${projectId}/member-management`,
      },
      contractorManagement: {
        path: '/projects/:projectId/contractor-management',
        getHref: (projectId: string | number) =>
          `/projects/${projectId}/contractor-management`,
      },
      trackingReportHistory: {
        path: '/projects/:projectId/tracking-report-history',
        getHref: (projectId: string) =>
          `/projects/${projectId}/tracking-report-history`,
      },
    },
    subproject: {
      hub: {
        path: '/projects/:projectId/subprojects',
        getHref: (projectId: number) => `/projects/${projectId}/subprojects`,
      },
      create: {
        path: '/projects/:projectId/subproject/create',
        getHref: (projectId: number) =>
          `/projects/${projectId}/subproject/create`,
      },
    },
    manageMemberInvitation: {
      path: '/manage-project-invitation',
      getHref: (invitationToken: string) =>
        '/manage-project-invitation?invitationToken=' + invitationToken,
    },
    claimOwnership: {
      path: '/claim-project-ownership',
      getHref: (invitationToken: string) =>
        '/claim-project-ownership?token=' + invitationToken,
    },
  },
  dashboard: {
    path: '/dashboard',
    getHref: () => '/dashboard',
  },
  insights: {
    path: '/insights',
    getHref: (
      params:
        | { kpiUniqueId?: string; projectId: string }
        | undefined = undefined,
    ) => {
      if (!params) return '/insights';
      const sp = new URLSearchParams();
      if (params.kpiUniqueId) sp.set('kpiUniqueId', params.kpiUniqueId);
      sp.set('projectId', params.projectId);
      return `/insights?${sp.toString()}`;
    },
  },
  forecast: {
    path: '/forecast',
    getHref: () => '/forecast',
  },
  documents: {
    path: '/documents',
    getHref: () => '/documents',
  },
  notifications: {
    path: '/notifications',
    getHref: () => '/notifications',
  },
  partners: {
    hub: {
      path: '/partners',
      getHref: () => '/partners',
    },
    manageInvitation: {
      path: '/manage-partner-invitation',
      getHref: (invitationToken: string) =>
        '/manage-partner-invitation?token=' + invitationToken,
    },
    acceptInvitation: {
      path: '/accept-partner-invitation',
      getHref: (invitationToken: string) =>
        '/accept-partner-invitation?token=' + invitationToken,
    },
    acceptContractorSubproject: {
      path: '/accept-contractor-subproject',
      getHref: (invitationToken: string) =>
        '/accept-contractor-subproject?token=' + invitationToken,
    },
  },
  settings: {
    path: '/settings',
    getHref: () => '/settings',
  },
  plansManagement: {
    path: '/plans-management',
    getHref: (tab?: 'org' | 'project') => {
      const queryParams = new URLSearchParams();
      if (tab) {
        queryParams.append('tab', tab);
      }
      const query = queryParams.toString();
      return `/plans-management${query ? `?${query}` : ''}`;
    },
  },
  checkoutSuccess: {
    path: '/checkout-success',
    getHref: (params?: {
      planType?: string;
      hasTrial?: boolean;
      billingPeriod?: string;
    }) => {
      const queryParams = new URLSearchParams();
      if (params?.planType) queryParams.append('plan_type', params.planType);
      if (params?.hasTrial !== undefined)
        queryParams.append('has_trial', String(params.hasTrial));
      if (params?.billingPeriod)
        queryParams.append('billing_period', params.billingPeriod);
      const query = queryParams.toString();
      return `/checkout-success${query ? `?${query}` : ''}`;
    },
  },
} as const;

export const createOrgRedirectExceptions: string[] = [
  paths.org.manageInvitation.path,
  paths.partners.manageInvitation.path,
  paths.partners.acceptInvitation.path,
  paths.partners.acceptContractorSubproject.path,
];
